import moment from 'moment-timezone';

// All displayed dates in the portal are rendered in Mountain Time
// (America/Denver) regardless of the viewer's local timezone or the
// server timezone. Use this constant everywhere a date is formatted for
// display so the behavior stays consistent across the app.
export const MOUNTAIN_TZ = 'America/Denver';

// Extract the LITERAL calendar date (MM/DD/YYYY) from a date-ish value WITHOUT
// any timezone conversion. Naive calendar dates — the WAC effective/increase
// date, the FDA approval date, the patent expiration date — carry no
// time-of-day and no meaningful timezone; they are the day the user typed.
// Running them through moment().tz() (or Date#toISOString) shifts them a
// calendar day (or two, depending on the viewer's browser timezone) because a
// naive date gets reinterpreted as an instant and then re-zoned. Comparing /
// formatting by the literal calendar day keeps the value stable end to end.
//   - A JS Date (e.g. exceljs returns a UTC-midnight Date for date cells): use
//     its UTC components — the same convention the report layer uses.
//   - A string: read the literal MM/DD/YYYY or YYYY-MM-DD date portion as-is.
// Returns '' when no calendar date can be recognized.
export const toCalendarDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const mm = String(value.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(value.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}/${value.getUTCFullYear()}`;
  }
  const s = String(value).trim();
  // MM/DD/YYYY (optionally followed by a time component).
  const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (mdy) return `${mdy[1].padStart(2, '0')}/${mdy[2].padStart(2, '0')}/${mdy[3]}`;
  // YYYY-MM-DD (optionally followed by a time / zone component).
  const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (ymd) return `${ymd[2].padStart(2, '0')}/${ymd[3].padStart(2, '0')}/${ymd[1]}`;
  return '';
};

// The same literal, timezone-free calendar date as toCalendarDate, formatted as
// YYYY-MM-DD (used by the report date-range payload, which the report function
// parses by calendar day). No timezone conversion — the day the user typed is
// preserved verbatim.
export const toIsoCalendarDate = (value) => {
  const mdy = toCalendarDate(value);
  if (!mdy) return '';
  const [mm, dd, yyyy] = mdy.split('/');
  return `${yyyy}-${mm}-${dd}`;
};

export default {
  formatDateTimeFromISO: (iso) =>
    moment(iso).tz(MOUNTAIN_TZ).format('MM/DD/Y hh:mm:ss'),
  formatDateFromISO: (iso) => moment(iso).tz(MOUNTAIN_TZ).format('MM/DD/Y'),
  formatDayDateFromISO: (iso) =>
    moment(iso).tz(MOUNTAIN_TZ).format('dddd MM/DD/Y'),
  formatDayDateTimeFromISO: (iso) =>
    moment(iso).tz(MOUNTAIN_TZ).format('dddd MM/DD/Y hh:mm A'),
  convert: (iso) => moment(iso).tz(MOUNTAIN_TZ).format('MM/DD/YYYY'),
  toCalendarDate,
  toIsoCalendarDate,
};
